(() => {
  if (customElements.get('sticky-footer-bar')) return;

  class StickyFooterBar extends HTMLElement {
    constructor() {
      super();
      this.buyBox = null;
      this.footer = null;
      this.contentObserver = null;
      this.resizeObserver = null;
      this.cartObserver = null;
      this.rafId = null;

      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = `
        <style>
          :host {
            position: fixed;
            bottom: 0;
            left: 0;
            width: 100%;
            transform: translateY(100%);
            opacity: 0;
            pointer-events: none;
            transition: transform 0.35s ease, opacity 0.35s ease;
            z-index: 9999;
          }

          :host(.visible) {
            transform: translateY(0);
            opacity: 1;
            pointer-events: auto;
          }

          ::slotted(*) {
            width: 100%;
          }
        </style>
        <slot name="bar"></slot>
      `;
    }

    connectedCallback() {
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onBarClick = this.onBarClick.bind(this);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.addEventListener('click', this.onBarClick);
      document.addEventListener('change', this.onContentChange);
      document.addEventListener('theme:variant:change', this.onContentChange);

      this.initContentObserver();
      this.initCartObserver();
      this.scheduleUpdate();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.removeEventListener('click', this.onBarClick);
      document.removeEventListener('change', this.onContentChange);
      document.removeEventListener('theme:variant:change', this.onContentChange);

      if (this.contentObserver) this.contentObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.cartObserver) this.cartObserver.disconnect();
      if (this.rafId) window.cancelAnimationFrame(this.rafId);
    }

    onContentChange = () => {
      this.syncBarContent();
      this.scheduleUpdate();
    };

    onScroll() {
      this.scheduleUpdate();
    }

    onResize() {
      this.buyBox = null;
      this.footer = null;
      this.scheduleUpdate();
    }

    scheduleUpdate() {
      if (this.rafId) return;
      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = null;
        this.updateVisibility();
        this.syncBarContent();
        this.updateBarHeightVar();
      });
    }

    getHeaderOffset() {
      const selector = this.getAttribute('top-bar-selector');
      const header = selector ? document.querySelector(selector) : null;
      return header ? header.offsetHeight : 0;
    }

    isVisibleElement(element) {
      if (!element) return false;
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      return element.getClientRects().length > 0;
    }

    getBuyBox() {
      if (this.buyBox && document.contains(this.buyBox) && this.isVisibleElement(this.buyBox)) {
        return this.buyBox;
      }

      const customSelector = this.getAttribute('hide-when-visible');
      const selectors = [
        'loop-subscription-widget-manual',
        '.ab-test-supply-selector-widget',
        '.product__submit',
        '[data-product-form]',
        customSelector,
      ].filter(Boolean);

      for (const selector of selectors) {
        const candidate = document.querySelector(selector);
        if (this.isVisibleElement(candidate)) {
          this.buyBox = candidate;
          return candidate;
        }
      }

      const visibleProductForm = Array.from(document.querySelectorAll('product-form')).find((element) =>
        this.isVisibleElement(element),
      );

      this.buyBox = visibleProductForm || null;
      return this.buyBox;
    }

    isBuyBoxVisible() {
      const buyBox = this.getBuyBox();
      if (!buyBox) return false;

      const rect = buyBox.getBoundingClientRect();
      const headerOffset = this.getHeaderOffset();
      return rect.bottom > headerOffset + 8 && rect.top < window.innerHeight - 8;
    }

    getFooter() {
      if (this.footer && document.contains(this.footer) && this.isVisibleElement(this.footer)) {
        return this.footer;
      }

      const customSelector = this.getAttribute('hide-at-footer');
      const selectors = [
        customSelector,
        'footer.footer-sections',
        '.site-footer-wrapper',
        'footer.site-footer-wrapper',
      ].filter(Boolean);

      for (const selector of selectors) {
        const candidate = document.querySelector(selector);
        if (this.isVisibleElement(candidate)) {
          this.footer = candidate;
          return candidate;
        }
      }

      this.footer = null;
      return null;
    }

    isFooterReached() {
      const footer = this.getFooter();
      if (!footer) return false;

      return footer.getBoundingClientRect().top < window.innerHeight;
    }

    isCartDrawerOpen() {
      return document.body.classList.contains('cart-drawer-v2-open')
        || Boolean(document.querySelector('cart-drawer-v2.is-open, cart-drawer-v2.is-closing'));
    }

    initCartObserver() {
      this.cartObserver = new MutationObserver(() => this.scheduleUpdate());
      this.cartObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    updateVisibility() {
      const shouldShow =
        window.scrollY > 80 &&
        !this.isBuyBoxVisible() &&
        !this.isCartDrawerOpen() &&
        !this.isFooterReached();
      this.classList.toggle('visible', shouldShow);
    }

    pageHasProduct() {
      if (this.dataset.pageProduct === 'true') return true;
      if (document.querySelector('[data-add-to-cart]')) return true;

      return Array.from(document.querySelectorAll('[data-replo-product-handle], [data-replo-product-container]')).some((element) => {
        const handle = (element.getAttribute('data-replo-product-handle') || '').trim();
        const id = (element.getAttribute('data-replo-product-container') || '').trim();
        return Boolean(handle || id);
      });
    }

    isFallbackMode() {
      return Boolean(this.dataset.fallbackUrl) && !this.pageHasProduct();
    }

    applyFallbackContent() {
      const titleEl = this.querySelector('[data-sticky-atc-title]');
      const cycleEl = this.querySelector('[data-sticky-atc-cycle]');
      const priceEl = this.querySelector('[data-sticky-atc-price]');
      const compareEl = this.querySelector('[data-sticky-atc-compare]');
      const priceWrap = this.querySelector('.ab-test-atc-price');
      const buttonTextEl = this.querySelector('[data-sticky-atc-button-text]');
      const imageEl = this.querySelector('[data-sticky-atc-image]');
      const button = this.querySelector('[data-sticky-atc-submit]');
      const infoEl = this.querySelector('[data-sticky-atc-info]');

      if (titleEl) titleEl.textContent = this.dataset.fallbackTitle || '';
      if (cycleEl) cycleEl.textContent = '';
      if (priceEl) priceEl.textContent = '';
      if (compareEl) {
        compareEl.textContent = '';
        compareEl.hidden = true;
      }
      if (priceWrap) priceWrap.hidden = true;
      if (buttonTextEl) buttonTextEl.textContent = 'Shop Toku Flow';
      if (button) button.disabled = false;
      if (infoEl) infoEl.setAttribute('aria-label', 'Shop Toku Flow');

      if (!imageEl) return;

      const fallbackSrc = this.dataset.fallbackImage || '';
      if (!fallbackSrc) {
        imageEl.hidden = true;
        return;
      }

      if (imageEl.dataset.appliedFallbackSrc !== fallbackSrc) {
        imageEl.src = fallbackSrc;
        imageEl.dataset.appliedFallbackSrc = fallbackSrc;
      }
      imageEl.hidden = false;
      if (titleEl) imageEl.alt = titleEl.textContent;
    }

    syncBarContent() {
      if (this.isFallbackMode()) {
        this.applyFallbackContent();
        return;
      }

      const titleEl = this.querySelector('[data-sticky-atc-title]');
      const cycleEl = this.querySelector('[data-sticky-atc-cycle]');
      const priceEl = this.querySelector('[data-sticky-atc-price]');
      const compareEl = this.querySelector('[data-sticky-atc-compare]');
      const priceWrap = this.querySelector('.ab-test-atc-price');
      const buttonTextEl = this.querySelector('[data-sticky-atc-button-text]');
      const imageEl = this.querySelector('[data-sticky-atc-image]');
      const button = this.querySelector('[data-sticky-atc-submit]');
      const infoEl = this.querySelector('[data-sticky-atc-info]');

      if (priceWrap) priceWrap.hidden = false;
      if (infoEl) infoEl.setAttribute('aria-label', 'View purchase options');

      if (!priceEl || !button) return;

      const selectedOption = document.querySelector('.loop-widget-purchase-option-selected');
      const mainButton = document.querySelector('[data-add-to-cart]');
      const mainPrice = document.querySelector('[data-add-to-cart-current-price]');
      const mainCompare = document.querySelector('[data-add-to-cart-compare-price]');

      if (titleEl && !titleEl.textContent.trim()) {
        titleEl.textContent = this.dataset.productTitle || '';
      }

      let cycleText = '';
      let priceText = mainPrice?.textContent.trim() || '';
      let compareText = mainCompare?.textContent.trim() || '';
      let compareHidden = mainCompare?.hidden;

      if (selectedOption) {
        const optionLabel = selectedOption.querySelector('[data-loop-subscription-option-label]');
        const planPrice = selectedOption.querySelector('[data-loop-plan-price]');
        const comparePrice = selectedOption.querySelector('[data-loop-compare-price]');

        cycleText = optionLabel?.textContent.trim() || '';
        priceText = planPrice?.textContent.trim() || priceText;
        compareText = comparePrice?.textContent.trim() || compareText;
        compareHidden =
          !compareText ||
          comparePrice?.classList.contains('hidden') ||
          comparePrice?.hidden ||
          compareHidden;
      }

      if (cycleEl) cycleEl.textContent = cycleText;
      priceEl.textContent = priceText;

      if (compareEl) {
        compareEl.textContent = compareText;
        compareEl.hidden = compareHidden || !compareText;
      }

      if (buttonTextEl) {
        buttonTextEl.textContent = mainButton?.disabled ? 'Sold out' : 'Add to cart';
      }

      button.disabled = Boolean(mainButton?.disabled);
      this.syncProductImage(imageEl);
    }

    syncProductImage(imageEl) {
      if (!imageEl) return;

      const fallbackSrc = this.dataset.productImage || '';
      const activeImage = document.querySelector(
        '.product__slide.media--active img, [data-product-media-list] .media--active img, [data-product-image] img',
      );
      const nextSrc = activeImage?.currentSrc || activeImage?.src || fallbackSrc;

      if (!nextSrc) {
        imageEl.hidden = true;
        return;
      }

      if (imageEl.src !== nextSrc) imageEl.src = nextSrc;
      imageEl.hidden = false;
    }

    initContentObserver() {
      const targets = [
        document.querySelector('loop-subscription-widget-manual'),
        document.querySelector('[data-add-to-cart-price]'),
        document.querySelector('[data-add-to-cart-text]'),
      ].filter(Boolean);

      if (!targets.length) return;

      this.contentObserver = new MutationObserver(() => {
        this.syncBarContent();
      });

      targets.forEach((target) => {
        this.contentObserver.observe(target, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['hidden', 'class'],
        });
      });
    }

    updateBarHeightVar() {
      const height = this.offsetHeight || 54;
      document.documentElement.style.setProperty('--sticky-atc-height', `${height}px`);
    }

    scrollToBuyBox() {
      const buyBox = this.getBuyBox();
      if (!buyBox) return;

      const rect = buyBox.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const headerOffset = this.getHeaderOffset();
      const targetPosition = scrollTop + rect.top - headerOffset - 16;

      window.scrollTo({ top: Math.max(0, targetPosition), behavior: 'smooth' });
    }

    onBarClick(event) {
      if (this.isFallbackMode()) {
        const interactive = event.target.closest('[data-sticky-atc-info], [data-sticky-atc-submit]');
        if (!interactive || !this.dataset.fallbackUrl) return;

        event.preventDefault();
        window.location.href = this.dataset.fallbackUrl;
        return;
      }

      if (event.target.closest('[data-sticky-atc-info]')) {
        event.preventDefault();
        this.scrollToBuyBox();
        return;
      }

      const submitButton = event.target.closest('[data-sticky-atc-submit]');
      if (!submitButton || submitButton.disabled) return;

      event.preventDefault();

      const mainButton = document.querySelector('[data-add-to-cart]:not([disabled])');
      if (mainButton) {
        mainButton.click();
        return;
      }

      this.scrollToBuyBox();
    }
  }

  customElements.define('sticky-footer-bar', StickyFooterBar);
})();
